import { useCallback, useEffect, useReducer, useRef } from "react";
import { orderInvoiceSummaryService as defaultService } from "../services/orderInvoiceSummaryService";

// ─── State Machine ────────────────────────────────────────────────────────────

const INITIAL_STATE = { status: "idle", data: null, error: null };

function reducer(state, action) {
  switch (action.type) {
    case "FETCH_START":
      return { ...state, status: "loading", error: null };
    case "REFRESH_START":
      return { ...state, status: "refreshing", error: null };
    case "FETCH_SUCCESS":
      return { status: "success", data: action.payload, error: null };
    case "FETCH_ERROR":
      return { ...state, status: "error", error: action.payload };
    default:
      return { ...state };
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Low-level composable hook. Fetches order_invoice_summary whenever `params`
 * reference changes. Callers must memoize `params`.
 *
 * Race condition guard: requestId ref discards results from stale requests.
 * `service` is injected for testability.
 */
export function useGetOrderInvoiceSummary(params, service = defaultService) {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const hasLoadedOnce = useRef(false);
  const requestId = useRef(0);

  const execute = useCallback(
    async forceRefresh => {
      // eslint-disable-next-line no-plusplus
      const currentId = ++requestId.current;
      dispatch({
        type:
          forceRefresh || hasLoadedOnce.current
            ? "REFRESH_START"
            : "FETCH_START",
      });
      try {
        const summary = await service.getOrderInvoiceSummary(params);
        if (currentId !== requestId.current) return;
        hasLoadedOnce.current = true;
        dispatch({ type: "FETCH_SUCCESS", payload: summary });
      } catch (err) {
        if (currentId !== requestId.current) return;
        dispatch({ type: "FETCH_ERROR", payload: err });
      }
    },
    [service, params],
  );

  useEffect(() => {
    execute(false);
  }, [execute]);

  const refresh = useCallback(() => execute(true), [execute]);

  return {
    status: state.status,
    invoice: state.data,
    error: state.error,
    refresh,
  };
}

export default {};
